// What are we playing — today's daily, an old one, or a template on its own?
//
// **The only thing that counts is today's daily**, and that is the whole reason this module exists
// as a pure function rather than a couple of `if`s in main.ts. An archive or practice run that
// quietly posted a score to the hub, or extended a streak, would corrupt the one number the game is
// actually keeping. `isRanked` is the single place that decision is made, and it is unit-tested.
//
// Mode comes from the URL so a run is linkable: someone can send a friend the exact day or layout
// they are talking about, which is most of the point of having an archive at all.

import { TEMPLATES } from '../engine/templates'

export type PlayMode =
  | { kind: 'daily'; key: string }
  | { kind: 'archive'; key: string }
  | { kind: 'practice'; templateId: string; index: number }
  /**
   * The same puzzle again, for no credit.
   *
   * This is what "play again" offers after a run, and it is deliberately NOT another go at the
   * ranked daily. Locally that would be harmless — `recordRun` keeps the better score and touches no
   * counter for a day already played — but the hub takes a score per submission, so a replay button
   * that re-submitted would be a leaderboard you can farm by pressing a button until you like the
   * number. Unranked is the only version of "again" that is safe to put on screen.
   */
  | { kind: 'rerun'; key: string }

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/

/**
 * Read the mode out of a query string.
 *
 * Anything unrecognised falls back to today's daily rather than erroring. A bad link should drop you
 * into the game, not onto a message — the same reasoning as `/auth/hub` always redirecting to `/`.
 */
export function parseMode(search: string, todayKey: string): PlayMode {
  const q = new URLSearchParams(search)

  const practice = q.get('practice')
  if (practice) {
    if (!TEMPLATES.some((t) => t.id === practice)) return { kind: 'daily', key: todayKey }
    const n = Number(q.get('n') ?? 0)
    const index = Number.isInteger(n) && n >= 0 && n < 1000 ? n : 0
    return { kind: 'practice', templateId: practice, index }
  }

  // Same puzzle, no credit. Unlike `?day=`, today's own key IS allowed here — replaying today for
  // nothing is the entire point of the mode.
  const rerun = q.get('rerun')
  if (rerun && DATE_RE.test(rerun) && !Number.isNaN(Date.parse(`${rerun}T00:00:00Z`))) {
    if (rerun > todayKey) return { kind: 'daily', key: todayKey }
    return { kind: 'rerun', key: rerun }
  }

  const day = q.get('day')
  if (day && DATE_RE.test(day) && !Number.isNaN(Date.parse(`${day}T00:00:00Z`))) {
    // Today's date via ?day= is still the daily — it is the same puzzle, and treating it as archive
    // would be a way to play the real thing without it counting.
    if (day === todayKey) return { kind: 'daily', key: todayKey }
    // The future does not exist yet. Without this, `?day=2030-01-01` hands out a puzzle nobody has
    // been set, and a score for it.
    if (day > todayKey) return { kind: 'daily', key: todayKey }
    return { kind: 'archive', key: day }
  }

  return { kind: 'daily', key: todayKey }
}

/** Only today's daily counts — for the hub, for streaks, for stats. */
export function isRanked(mode: PlayMode): boolean {
  return mode.kind === 'daily'
}

/** The banner a non-daily run carries, so nobody is confused about whether it counted. */
export function modeLabel(mode: PlayMode): string {
  switch (mode.kind) {
    case 'daily':
      return ''
    case 'archive':
      return `ARCHIVE · ${mode.key}`
    case 'practice':
      return `PRACTICE · ${mode.templateId.replace(/-/g, ' ').toUpperCase()}`
    case 'rerun':
      return `AGAIN · ${mode.key} · DOES NOT COUNT`
  }
}

/**
 * The query string for "play this again", which is always the unranked form of what was just played.
 *
 * Pure, and here rather than in main.ts, for the same reason `isRanked` is: the rule that a replay
 * cannot count is one line, and it belongs next to the rule that says what counts.
 */
export function againSearch(mode: PlayMode): string {
  switch (mode.kind) {
    // A ranked day replays as a rerun of that same day. Never as the daily again.
    case 'daily':
      return `?rerun=${mode.key}`
    case 'archive':
      return `?day=${mode.key}`
    case 'rerun':
      return `?rerun=${mode.key}`
    case 'practice':
      return `?practice=${mode.templateId}&n=${mode.index}`
  }
}
